// AdminDashboard.js – all video AND audio uploads go directly to VPS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../supabaseClient';
import { CONTACT_EMAIL, LISTING_TYPES, EMPTY_GAMING_CARD, cardFromRow } from '../constants/gaming';
import { RESEARCH_CATEGORY, RESEARCH_ARTICLES, formatResearchDate } from '../constants/research';
import ResearchArticle from '../components/ResearchArticle';

// ────────────────────────────────────────────────
// Environment variables
// ────────────────────────────────────────────────
const VPS_UPLOAD_URL       = process.env.REACT_APP_VPS_UPLOAD_URL;        // /upload-video/
const VPS_AUDIO_UPLOAD_URL = process.env.REACT_APP_VPS_AUDIO_UPLOAD_URL;  // /upload-audio/
const VPS_API_KEY          = process.env.REACT_APP_VPS_API_KEY;
const VPS_BASE_URL         = process.env.REACT_APP_VPS_BASE_URL || 'https://upload.modmedianow.com';

if (process.env.NODE_ENV !== 'production') {
  if (!VPS_UPLOAD_URL || !VPS_API_KEY) {
    console.error('Missing VPS upload configuration in .env file');
  }
  if (!VPS_AUDIO_UPLOAD_URL) {
    console.warn('REACT_APP_VPS_AUDIO_UPLOAD_URL not set – audio will fall back to video endpoint');
  }
}

// ────────────────────────────────────────────────
// Styled Components
// ────────────────────────────────────────────────

const DashboardContainer = styled.div`
  background: linear-gradient(160deg, #05080f 0%, #0c1422 50%, #091018 100%);
  min-height: 100vh;
  color: #e2e8f0;
  font-family: 'Arial', sans-serif;
`;

const TopBar = styled.div`
  background: rgba(0,0,0,0.5);
  border-bottom: 1px solid rgba(0,212,255,0.12);
  padding: 20px 40px;
  display: flex; align-items: center; justify-content: space-between;
  position: sticky; top: 0; z-index: 100; backdrop-filter: blur(16px);
  @media(max-width:600px){ padding: 14px 16px; }
`;
const TopBarTitle = styled.div`
  h1 { font-size: 1.1rem; color: #00d4ff; letter-spacing: 2px; text-transform: uppercase; margin: 0;
       font-weight: 700; span { color: rgba(255,255,255,0.35); margin-right: 8px; } }
`;
const LogoutBtn = styled.button`
  background: rgba(229,62,62,0.12); border: 1px solid rgba(229,62,62,0.3);
  color: rgba(255,120,120,0.9); padding: 7px 18px; border-radius: 7px;
  cursor: pointer; font-size: 0.8rem; letter-spacing: 1px; text-transform: uppercase;
  transition: all 0.2s; &:hover{ background: rgba(229,62,62,0.25); }
`;
const TabNav = styled.div`
  display: flex; overflow-x: auto; border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(0,0,0,0.3); padding: 0 40px; gap: 2px;
  @media(max-width:600px){ padding: 0 10px; }
  &::-webkit-scrollbar{ height: 0; }
`;
const NavTab = styled.button`
  padding: 15px 22px; background: none; border: none;
  border-bottom: 2px solid ${p=>p.$active?'#00d4ff':'transparent'};
  color: ${p=>p.$active?'#00d4ff':'rgba(255,255,255,0.4)'};
  font-size: 0.8rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
  cursor: pointer; transition: all 0.2s; white-space: nowrap;
  &:hover{ color: rgba(255,255,255,0.75); }
`;
const TabContent = styled.div`max-width:920px;margin:0 auto;padding:36px 40px 0;animation:fadeIn 0.3s ease;
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
  @media(max-width:600px){ padding: 20px 14px 0; }
`;
const SectionInner = styled.div``;

const Section = styled.section`
  background: rgba(26, 32, 44, 0.95);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(74, 85, 104, 0.3);
  backdrop-filter: blur(10px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  }
`;

const SectionHeader = styled.h3`
  color: #63b3ed;
  margin-bottom: 24px;
  font-size: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #2d3748;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: "▸";
    color: #4299e1;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #a0aec0;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 1px solid #4a5568;
  border-radius: 10px;
  background: rgba(45, 55, 72, 0.8);
  color: #e2e8f0;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
    background: rgba(45, 55, 72, 1);
  }
  
  &::placeholder {
    color: #718096;
  }
`;

const Textarea = styled.textarea`
  padding: 14px 16px;
  border: 1px solid #4a5568;
  border-radius: 10px;
  background: rgba(45, 55, 72, 0.8);
  color: #e2e8f0;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
    background: rgba(45, 55, 72, 1);
  }
  
  &::placeholder {
    color: #718096;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66, 153, 225, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ArchiveButton = styled(Button)`
  background: linear-gradient(135deg, #f6ad55 0%, #ed8936 100%);
  box-shadow: 0 4px 15px rgba(246, 173, 85, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
    box-shadow: 0 6px 20px rgba(246, 173, 85, 0.4);
  }
`;

const DeleteButton = styled(Button)`
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  box-shadow: 0 4px 15px rgba(229, 62, 62, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #c53030 0%, #9b2c2c 100%);
    box-shadow: 0 6px 20px rgba(229, 62, 62, 0.4);
  }
`;

const ListItem = styled.div`
  background: linear-gradient(135deg, rgba(45, 55, 72, 0.8) 0%, rgba(74, 85, 104, 0.6) 100%);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(74, 85, 104, 0.4);
  transition: all 0.3s ease;
  gap: 16px;

  &:hover {
    background: linear-gradient(135deg, rgba(45, 55, 72, 1) 0%, rgba(74, 85, 104, 0.8) 100%);
    border-color: #4a5568;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ItemTitle = styled.span`
  color: #e2e8f0;
  font-weight: 500;
  font-size: 1.1rem;
  flex: 1;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 12px;
    justify-content: flex-end;
  }
`;

const DashboardHeader = styled.h2`
  color: #63b3ed;
  margin-bottom: 40px;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 10px rgba(99, 179, 237, 0.3);
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #4299e1, #63b3ed);
    border-radius: 2px;
  }
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #a0aec0;
  font-size: 0.9rem;
  font-weight: 500;
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 4px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 0.9rem;
  cursor: pointer;

  input {
    accent-color: #4299e1;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const FieldHint = styled.p`
  color: #718096;
  font-size: 0.78rem;
  margin: 2px 0 0;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #a0aec0;
  background: rgba(160, 174, 192, 0.12);
  border: 1px solid rgba(160, 174, 192, 0.28);
  white-space: nowrap;
`;

const LISTING_TYPE_LABELS = {
  [LISTING_TYPES.EBAY]: 'eBay / store listing',
  [LISTING_TYPES.LOCAL]: 'Local only (contact by email)',
  [LISTING_TYPES.SHOWCASE]: 'Showcase (not for sale)',
};

const FormError = styled.p`
  color: #feb2b2;
  background: rgba(229, 62, 62, 0.12);
  border: 1px solid rgba(229, 62, 62, 0.35);
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin: 0;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  background: rgba(45, 55, 72, 0.8);
  border-radius: 8px;
  overflow: hidden;
  height: 10px;
  margin-top: 8px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 8px;
  background: linear-gradient(90deg, #4299e1, #63b3ed);
  transition: width 0.3s ease;
  width: ${({ $pct }) => $pct}%;
`;

const ProgressLabel = styled.p`
  color: #a0aec0;
  font-size: 0.82rem;
  margin: 4px 0 0;
  text-align: right;
`;

// ────────────────────────────────────────────────
// Upload constants
// ────────────────────────────────────────────────
const MAX_VIDEO_MB = 600;
const MAX_AUDIO_MB = 20;
const CHUNK_SIZE   = 5 * 1024 * 1024; // 5 MB per chunk
const MAX_RETRIES  = 3;
const RETRY_DELAY  = 2000;

const genFileId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [homeVideo,     setHomeVideo]     = useState({ title: '', file: null, subheader: '' });
  const [homeImages,    setHomeImages]    = useState({ image: null, linkUrl: '' });
  const [featuredVideo, setFeaturedVideo] = useState({ src: '', title: '', subheader: '', isTop: false });
  const [videos,        setVideos]        = useState({ title: '', file: null, embedUrl: '', description: '', date: '', rumbleUrl: '', section: 'crypto-crossfire' });
  const [articles,      setArticles]      = useState({ articleId: '', title: '', subheader: '', content: '', image: null });

  const [homeContent,  setHomeContent]  = useState([]);
  const [videoList,    setVideoList]    = useState([]);
  const [articleList,  setArticleList]  = useState([]);

  const [loading,          setLoading]          = useState(false);
  const [uploadProgress,   setUploadProgress]   = useState(0);   // video progress
  const [audioProgress,    setAudioProgress]    = useState(0);   // audio progress
  const [activeTab,        setActiveTab]        = useState('home');
  const [openResearchSlug, setOpenResearchSlug] = useState(null);

  // Radio
  const [radioTrack, setRadioTrack] = useState({ title:'', description:'', station:'crypto-crossfire', file_type:'audio', file:null });
  const [radioList,  setRadioList]  = useState([]);
  // Gaming
  const [gamingCard,  setGamingCard]  = useState(EMPTY_GAMING_CARD);
  const [gamingList,  setGamingList]  = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [gamingFormError, setGamingFormError] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/admin/login', { replace: true });
        return;
      }
      fetchContent();
    });
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const { data: homeData,    error: homeError    } = await supabase.from('home_content').select('*');
      if (homeError) throw homeError;
      const { data: videoData,   error: videoError   } = await supabase.from('videos').select('*');
      if (videoError) throw videoError;
      const { data: articleData, error: articleError } = await supabase.from('articles').select('*');
      if (articleError) throw articleError;

      setHomeContent(homeData || []);
      setVideoList(videoData || []);
      setArticleList(articleData || []);

      const { data: radioData  } = await supabase.from('radio_tracks').select('*').order('created_at',{ascending:false});
      setRadioList(radioData || []);
      const { data: gamingData } = await supabase.from('gaming_cards').select('*').order('sort_order',{ascending:true});
      setGamingList(gamingData || []);

      const existingFeatured = homeData?.find((c) => c.type === 'featured_video');
      if (existingFeatured) {
        setFeaturedVideo((prev) => ({ ...prev, isTop: existingFeatured.is_top || false }));
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching content:', err);
    }
  };

  const handleFileChange = (e, setter, field) => {
    setter((prev) => ({ ...prev, [field]: e.target.files?.[0] || null }));
  };

  // ── Generic chunk sender ──────────────────────────────────────────────────
  const sendChunk = (chunk, index, totalChunks, fileId, fileName, uploadUrl, attempt = 0) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', new File([chunk], fileName));

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);
      xhr.setRequestHeader('X-API-KEY',      VPS_API_KEY);
      xhr.setRequestHeader('X-File-Id',      fileId);
      xhr.setRequestHeader('X-Chunk-Index',  index);
      xhr.setRequestHeader('X-Total-Chunks', totalChunks);
      xhr.setRequestHeader('X-File-Name',    fileName);
      xhr.timeout = 2 * 60 * 1000; // 2 min per chunk

      const retry = (err) => {
        if (attempt < MAX_RETRIES) {
          if (process.env.NODE_ENV !== 'production') console.warn(`Chunk ${index} failed, retrying (${attempt + 1}/${MAX_RETRIES})…`);
          setTimeout(() => {
            sendChunk(chunk, index, totalChunks, fileId, fileName, uploadUrl, attempt + 1)
              .then(resolve).catch(reject);
          }, RETRY_DELAY * Math.pow(2, attempt));
        } else {
          reject(err);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Server returned non-JSON response.')); }
        } else if ([408, 502, 503].includes(xhr.status)) {
          retry(new Error(`Chunk ${index} got HTTP ${xhr.status}`));
        } else {
          reject(new Error(`Chunk ${index} failed (HTTP ${xhr.status}): ${xhr.responseText}`));
        }
      };
      xhr.onerror   = () => retry(new Error('Network error on chunk ' + index));
      xhr.ontimeout = () => retry(new Error('Timeout on chunk ' + index));

      xhr.send(formData);
    });

  // ── Upload to VPS (shared logic) ─────────────────────────────────────────
  const uploadToVPS = async (file, uploadUrl, maxMB, setProgress) => {
    if (!file) return null;
    if (!uploadUrl || !VPS_API_KEY) throw new Error('VPS upload not configured.');

    const fileMB = file.size / (1024 * 1024);
    if (fileMB > maxMB) {
      throw new Error(`File is ${fileMB.toFixed(1)} MB — maximum allowed is ${maxMB} MB.`);
    }

    setProgress(0);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileId      = genFileId();
    let   lastResult  = {};

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      lastResult  = await sendChunk(chunk, i, totalChunks, fileId, file.name, uploadUrl);
      setProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    const url =
      lastResult.url || lastResult.fileUrl ||
      (lastResult.path
        ? `${VPS_BASE_URL}${lastResult.path.startsWith('/') ? '' : '/'}${lastResult.path}`
        : null);

    if (!url) throw new Error('Server did not return a file URL after assembly.');
    return url;
  };

  // Convenience wrappers
  const uploadVideoToVPS = (file) =>
    uploadToVPS(file, VPS_UPLOAD_URL, MAX_VIDEO_MB, setUploadProgress);

  const uploadAudioToVPS = (file) =>
    uploadToVPS(file, VPS_AUDIO_UPLOAD_URL || VPS_UPLOAD_URL, MAX_AUDIO_MB, setAudioProgress);

  // ── Main form submit handler ──────────────────────────────────────────────
  const handleSubmit = async (e, type, formData, setter) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      let fileUrl = null;

      // 1. Video uploads to VPS (home video + videos tab)
      if ((type === 'homeVideo' || type === 'video') && formData.file) {
        fileUrl = await uploadVideoToVPS(formData.file);
      }

      // 2. Home image → Supabase Storage
      if (type === 'homeImage' && formData.image) {
        const fileExt = formData.image.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('home-images')
          .upload(fileName, formData.image, { upsert: true, contentType: formData.image.type });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('home-images').getPublicUrl(fileName);
        fileUrl = urlData.publicUrl;
      }

      // 3. Article image → Supabase Storage
      let articleImgUrl = null;
      if (type === 'article' && formData.image) {
        const fileExt = formData.image.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('article-images')
          .upload(fileName, formData.image, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('article-images').getPublicUrl(fileName);
        articleImgUrl = urlData.publicUrl;
      }

      // 4. Save to DB
      let error = null;

      if (type === 'homeImage') {
        const existing = homeContent.find((c) => c.type === 'image');
        ({ error } = await supabase.from('home_content').upsert({
          id: existing?.id,
          type: 'image',
          file_url: fileUrl || existing?.file_url,
          link_url: formData.linkUrl?.trim() || existing?.link_url,
        }, { onConflict: 'id' }));
      } else if (type === 'homeVideo') {
        const existing = homeContent.find((c) => c.type === 'video');
        ({ error } = await supabase.from('home_content').upsert({
          id: existing?.id,
          type: 'video',
          title: formData.title.trim(),
          file_url: fileUrl || existing?.file_url,
          subheader: formData.subheader.trim(),
        }, { onConflict: 'id' }));
      } else if (type === 'featuredVideo') {
        const existing = homeContent.find((c) => c.type === 'featured_video');
        ({ error } = await supabase.from('home_content').upsert({
          id: existing?.id,
          type: 'featured_video',
          src: formData.src.trim(),
          title: formData.title.trim(),
          subheader: formData.subheader.trim(),
          is_top: formData.isTop,
        }, { onConflict: 'id' }));
      } else if (type === 'video') {
        ({ error } = await supabase.from('videos').insert({
          title: formData.title.trim(),
          file_url: fileUrl,
          embed_url: formData.embedUrl?.trim() || null,
          description: formData.description.trim(),
          date: formData.date || null,
          rumble_url: formData.rumbleUrl?.trim() || null,
          section: formData.section || 'crypto-crossfire',
        }));
      } else if (type === 'article') {
        ({ error } = await supabase.from('articles').insert({
          article_id: formData.articleId.trim(),
          title: formData.title.trim(),
          subheader: formData.subheader.trim(),
          content: formData.content.trim(),
          img_src: articleImgUrl,
        }));
      }

      if (error) throw error;

      alert('Content saved successfully!');
      await fetchContent();

      setter(
        type === 'homeVideo'
          ? { title: '', file: null, subheader: '' }
          : type === 'homeImage'
          ? { image: null, linkUrl: '' }
          : type === 'featuredVideo'
          ? { src: '', title: '', subheader: '', isTop: false }
          : type === 'video'
          ? { title: '', file: null, embedUrl: '', description: '', date: '', rumbleUrl: '', section: 'crypto-crossfire' }
          : { articleId: '', title: '', subheader: '', content: '', image: null }
      );
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Submit error:', err);
      alert(`Failed to save content: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (table, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      alert('Deleted successfully');
      fetchContent();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const handleArchive = async (article) => {
    if (!window.confirm(`Archive "${article.title}"?`)) return;
    try {
      const { error: insertError } = await supabase.from('archived_articles').insert({
        article_id: article.article_id,
        title: article.title,
        subheader: article.subheader,
        content: article.content,
        img_src: article.img_src,
        archived_at: new Date().toISOString(),
      });
      if (insertError) throw insertError;
      const { error: deleteError } = await supabase.from('articles').delete().eq('id', article.id);
      if (deleteError) throw deleteError;
      alert(`"${article.title}" archived successfully.`);
      fetchContent();
    } catch (err) {
      alert(`Failed to archive: ${err.message}`);
    }
  };

  const TABS = [
    {id:'home',    label:'🏠 Home Content'},
    {id:'videos',  label:'🎬 Videos'},
    {id:'articles',label:'📝 Articles'},
    {id:'radio',   label:'📻 Radio'},
    {id:'gaming',  label:'🎮 Gaming'},
    {id:'research',label:'🔬 Research'},
  ];

  return (
    <DashboardContainer>
      <TopBar>
        <TopBarTitle><h1><span>ModMedia</span> Admin Dashboard</h1></TopBarTitle>
        <LogoutBtn onClick={async () => { await supabase.auth.signOut(); navigate('/admin/login'); }}>Log Out</LogoutBtn>
      </TopBar>
      <TabNav>
        {TABS.map(t=>(
          <NavTab key={t.id} $active={activeTab===t.id} onClick={()=>setActiveTab(t.id)}>{t.label}</NavTab>
        ))}
      </TabNav>
      <TabContent>

      {/* ── HOME TAB ─────────────────────────────────────────────────────── */}
      {(activeTab==='home') && (<SectionInner>
      <Section>
        <SectionHeader>Home Page – Latest Submission Video</SectionHeader>
        <Form onSubmit={(e) => handleSubmit(e, 'homeVideo', homeVideo, setHomeVideo)}>
          <InputGroup>
            <Label>Video Title</Label>
            <Input type="text" placeholder="Enter video title" value={homeVideo.title}
              onChange={(e) => setHomeVideo({ ...homeVideo, title: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Video File (max {MAX_VIDEO_MB} MB)</Label>
            <Input type="file" accept="video/*"
              onChange={(e) => handleFileChange(e, setHomeVideo, 'file')}
              required={!homeContent.some((c) => c.type === 'video')} />
          </InputGroup>
          <InputGroup>
            <Label>Subheader (optional)</Label>
            <Input type="text" placeholder="Enter subheader" value={homeVideo.subheader}
              onChange={(e) => setHomeVideo({ ...homeVideo, subheader: e.target.value })} />
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? `Uploading… ${uploadProgress}%` : 'Upload Video'}
          </Button>
          {loading && homeVideo.file && (
            <>
              <ProgressBarWrapper><ProgressBarFill $pct={uploadProgress} /></ProgressBarWrapper>
              <ProgressLabel>{uploadProgress}% — uploading in chunks, please wait…</ProgressLabel>
            </>
          )}
        </Form>

        {homeContent.filter((c) => c.type === 'video').map((item) => (
          <ListItem key={item.id}>
            <ItemTitle>{item.title || '(no title)'}</ItemTitle>
            <DeleteButton onClick={() => handleDelete('home_content', item.id)}>Delete</DeleteButton>
          </ListItem>
        ))}
      </Section>

      <Section>
        <SectionHeader>Home Page – Bluesky Image</SectionHeader>
        <Form onSubmit={(e) => handleSubmit(e, 'homeImage', homeImages, setHomeImages)}>
          <InputGroup>
            <Label>Image File</Label>
            <Input type="file" accept="image/*"
              onChange={(e) => handleFileChange(e, setHomeImages, 'image')} />
          </InputGroup>
          <InputGroup>
            <Label>Bluesky Post Link</Label>
            <Input type="text" placeholder="Enter Bluesky post link" value={homeImages.linkUrl}
              onChange={(e) => setHomeImages({ ...homeImages, linkUrl: e.target.value })} />
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Uploading...' : 'Upload Image'}
          </Button>
        </Form>

        {homeContent.filter((c) => c.type === 'image').map((item) => (
          <ListItem key={item.id}>
            <ItemTitle>{item.link_url || 'No link set'}</ItemTitle>
            <DeleteButton onClick={() => handleDelete('home_content', item.id)}>Delete</DeleteButton>
          </ListItem>
        ))}
      </Section>

      <Section>
        <SectionHeader>Home Page – Featured Video</SectionHeader>
        <Form onSubmit={(e) => handleSubmit(e, 'featuredVideo', featuredVideo, setFeaturedVideo)}>
          <InputGroup>
            <Label>Rumble Embed URL</Label>
            <Input type="text" placeholder="Enter Rumble embed URL" value={featuredVideo.src}
              onChange={(e) => setFeaturedVideo({ ...featuredVideo, src: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Video Title</Label>
            <Input type="text" placeholder="Enter video title" value={featuredVideo.title}
              onChange={(e) => setFeaturedVideo({ ...featuredVideo, title: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Subheader (optional)</Label>
            <Input type="text" placeholder="Enter subheader" value={featuredVideo.subheader}
              onChange={(e) => setFeaturedVideo({ ...featuredVideo, subheader: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <ToggleLabel>
              Display at Top of Homepage
              <Input type="checkbox" checked={featuredVideo.isTop}
                onChange={(e) => setFeaturedVideo({ ...featuredVideo, isTop: e.target.checked })} />
            </ToggleLabel>
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Featured Video'}
          </Button>
        </Form>

        {homeContent.filter((c) => c.type === 'featured_video').map((item) => (
          <ListItem key={item.id}>
            <ItemTitle>{item.title || '(no title)'}</ItemTitle>
            <DeleteButton onClick={() => handleDelete('home_content', item.id)}>Delete</DeleteButton>
          </ListItem>
        ))}
      </Section>
      </SectionInner>)}

      {/* ── VIDEOS TAB ───────────────────────────────────────────────────── */}
      {activeTab==='videos' && (<SectionInner>
      <Section>
        <SectionHeader>Videos Section (max {MAX_VIDEO_MB} MB · VPS direct upload)</SectionHeader>
        <Form onSubmit={(e) => handleSubmit(e, 'video', videos, setVideos)}>
          <InputGroup>
            <Label>Section</Label>
            <select value={videos.section}
              onChange={(e) => setVideos({ ...videos, section: e.target.value })}
              style={{padding:'14px 16px',border:'1px solid #4a5568',borderRadius:'10px',
                      background:'rgba(45,55,72,0.8)',color:'#e2e8f0',fontSize:'1rem',width:'100%'}}>
              <option value="crypto-crossfire">🔥 Crypto Cross Fire</option>
              <option value="political-news">📰 Political News</option>
              <option value="music">🎵 Music</option>
            </select>
          </InputGroup>
          <InputGroup>
            <Label>Video Title</Label>
            <Input type="text" placeholder="Enter video title" value={videos.title}
              onChange={(e) => setVideos({ ...videos, title: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Video File (max {MAX_VIDEO_MB} MB)</Label>
            <Input type="file" accept="video/*"
              onChange={(e) => handleFileChange(e, setVideos, 'file')} />
          </InputGroup>
          <InputGroup>
            <Label>Rumble Embed URL (optional)</Label>
            <Input type="text" placeholder="Enter Rumble embed URL" value={videos.embedUrl}
              onChange={(e) => setVideos({ ...videos, embedUrl: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <Label>Description</Label>
            <Input type="text" placeholder="Enter description" value={videos.description}
              onChange={(e) => setVideos({ ...videos, description: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <Label>Date</Label>
            <Input type="date" value={videos.date}
              onChange={(e) => setVideos({ ...videos, date: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <Label>Rumble URL (optional)</Label>
            <Input type="text" placeholder="Enter Rumble URL" value={videos.rumbleUrl}
              onChange={(e) => setVideos({ ...videos, rumbleUrl: e.target.value })} />
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? `Uploading… ${uploadProgress}%` : 'Upload Video'}
          </Button>
          {loading && videos.file && (
            <>
              <ProgressBarWrapper><ProgressBarFill $pct={uploadProgress} /></ProgressBarWrapper>
              <ProgressLabel>{uploadProgress}% — uploading in chunks, please wait…</ProgressLabel>
            </>
          )}
        </Form>

        {videoList.map((video) => (
          <ListItem key={video.id}>
            <div>
              <ItemTitle>{video.title || '(no title)'}</ItemTitle>
              <p style={{color:'#718096',fontSize:'0.82rem',margin:'3px 0 0'}}>
                {video.section === 'crypto-crossfire' ? '🔥 Crypto Cross Fire'
                  : video.section === 'political-news' ? '📰 Political News'
                  : video.section === 'music' ? '🎵 Music'
                  : video.section || 'No section'}
              </p>
            </div>
            <DeleteButton onClick={() => handleDelete('videos', video.id)}>Delete</DeleteButton>
          </ListItem>
        ))}
      </Section>
      </SectionInner>)}

      {/* ── ARTICLES TAB ─────────────────────────────────────────────────── */}
      {activeTab==='articles' && (<SectionInner>
      <Section>
        <SectionHeader>Articles Section</SectionHeader>
        <Form onSubmit={(e) => handleSubmit(e, 'article', articles, setArticles)}>
          <InputGroup>
            <Label>Article ID (slug)</Label>
            <Input type="text" placeholder="e.g. free-speech, crypto-regulations"
              value={articles.articleId}
              onChange={(e) => setArticles({ ...articles, articleId: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Title</Label>
            <Input type="text" placeholder="Enter title" value={articles.title}
              onChange={(e) => setArticles({ ...articles, title: e.target.value })} required />
          </InputGroup>
          <InputGroup>
            <Label>Subheader (optional)</Label>
            <Input type="text" placeholder="Enter subheader" value={articles.subheader}
              onChange={(e) => setArticles({ ...articles, subheader: e.target.value })} />
          </InputGroup>
          <InputGroup>
            <Label>Article Image (optional)</Label>
            <Input type="file" accept="image/*"
              onChange={(e) => handleFileChange(e, setArticles, 'image')} />
          </InputGroup>
          <InputGroup>
            <Label>Article Content</Label>
            <Textarea placeholder="Enter article content (markdown supported)" rows={12}
              value={articles.content}
              onChange={(e) => setArticles({ ...articles, content: e.target.value })} required />
          </InputGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Publishing...' : 'Publish Article'}
          </Button>
        </Form>

        <div style={{ marginTop: '32px' }}>
          <h4 style={{ color: '#a0aec0', marginBottom: '16px' }}>Published Articles</h4>
          {articleList.map((article) => (
            <ListItem key={article.id}>
              <ItemTitle>{article.title}</ItemTitle>
              <ActionButtons>
                <ArchiveButton onClick={() => handleArchive(article)}>Archive</ArchiveButton>
                <DeleteButton onClick={() => handleDelete('articles', article.id)}>Delete</DeleteButton>
              </ActionButtons>
            </ListItem>
          ))}
          {articleList.length === 0 && (
            <p style={{ color: '#718096', textAlign: 'center', marginTop: '20px' }}>
              No published articles yet.
            </p>
          )}
        </div>
      </Section>

      <Section>
        <SectionHeader>Archived Content</SectionHeader>
        <p style={{ color: '#a0aec0', lineHeight: 1.6 }}>
          Archived articles are moved to the <code>archived_articles</code> table and are no longer
          visible on the home page.
          <br /><br />
          <small>
            • You can view them directly in the Supabase dashboard or via the article viewer page.
          </small>
        </p>
      </Section>
      </SectionInner>)}

      {/* ── RADIO TAB ────────────────────────────────────────────────────── */}
      {activeTab==='radio' && (
        <SectionInner>
          <Section>
            {/* MP3 file_type only – videos still go via the video tab */}
            <SectionHeader>Upload Radio Track (MP3 · max {MAX_AUDIO_MB} MB · VPS direct)</SectionHeader>
            <Form onSubmit={async (e) => {
              e.preventDefault();
              if (loading) return;
              if (!radioTrack.file) { alert('Select an MP3 file.'); return; }

              const fileMB = radioTrack.file.size / (1024 * 1024);
              if (fileMB > MAX_AUDIO_MB) {
                alert(`File is ${fileMB.toFixed(1)} MB — maximum is ${MAX_AUDIO_MB} MB for audio.`);
                return;
              }

              setLoading(true);
              setAudioProgress(0);
              try {
                // Upload MP3 directly to VPS in chunks
                const fileUrl = await uploadAudioToVPS(radioTrack.file);

                // Save metadata to Supabase (no file stored there anymore)
                const { error: dbErr } = await supabase.from('radio_tracks').insert({
                  title:       radioTrack.title.trim(),
                  description: radioTrack.description.trim(),
                  station:     radioTrack.station,
                  file_type:   'audio',
                  file_url:    fileUrl,
                  file_path:   null, // stored on VPS, not Supabase storage
                });
                if (dbErr) throw dbErr;

                alert('Track uploaded to VPS!');
                setRadioTrack({ title:'', description:'', station:'crypto-crossfire', file_type:'audio', file:null });
                const { data } = await supabase.from('radio_tracks').select('*').order('created_at',{ascending:false});
                setRadioList(data || []);
              } catch (err) {
                alert('Upload failed: ' + err.message);
              } finally {
                setLoading(false);
                setAudioProgress(0);
              }
            }}>
              <InputGroup><Label>Station</Label>
                <select value={radioTrack.station}
                  onChange={e=>setRadioTrack({...radioTrack,station:e.target.value})}
                  style={{padding:'14px 16px',border:'1px solid #4a5568',borderRadius:'10px',
                          background:'rgba(45,55,72,0.8)',color:'#e2e8f0',fontSize:'1rem',width:'100%'}}>
                  <option value="crypto-crossfire">🔥 Crypto Cross Fire</option>
                  <option value="music">🎵 Music</option>
                  <option value="political-news">📰 Political News</option>
                </select>
              </InputGroup>
              <InputGroup><Label>Title *</Label>
                <Input type="text" placeholder="Episode or track title" value={radioTrack.title}
                  onChange={e=>setRadioTrack({...radioTrack,title:e.target.value})} required/>
              </InputGroup>
              <InputGroup><Label>Description (optional)</Label>
                <Textarea placeholder="Brief description..." value={radioTrack.description}
                  onChange={e=>setRadioTrack({...radioTrack,description:e.target.value})}/>
              </InputGroup>
              <InputGroup><Label>MP3 File * (max {MAX_AUDIO_MB} MB)</Label>
                <Input type="file" accept="audio/mp3,audio/mpeg"
                  onChange={e=>setRadioTrack({...radioTrack,file:e.target.files?.[0]||null})} required/>
              </InputGroup>
              <Button type="submit" disabled={loading}>
                {loading ? `Uploading… ${audioProgress}%` : '⬆ Upload Track to VPS'}
              </Button>
              {loading && radioTrack.file && (
                <>
                  <ProgressBarWrapper><ProgressBarFill $pct={audioProgress} /></ProgressBarWrapper>
                  <ProgressLabel>{audioProgress}% — uploading in chunks…</ProgressLabel>
                </>
              )}
            </Form>
          </Section>

          <Section>
            <SectionHeader>Uploaded Tracks ({radioList.length})</SectionHeader>
            {radioList.length===0 && (
              <p style={{color:'#718096',textAlign:'center',padding:'20px 0'}}>No tracks yet.</p>
            )}
            {radioList.map(t=>(
              <ListItem key={t.id}>
                <div>
                  <ItemTitle>{t.title}</ItemTitle>
                  <p style={{color:'#718096',fontSize:'0.82rem',margin:'3px 0 0'}}>
                    {t.station} · {t.file_type?.toUpperCase()} · VPS
                  </p>
                </div>
                <DeleteButton onClick={async()=>{
                  if (!window.confirm('Delete "'+t.title+'"?')) return;
                  // File lives on VPS – only remove the DB row here.
                  // To delete the VPS file itself, use the VPS cleanup script.
                  await supabase.from('radio_tracks').delete().eq('id',t.id);
                  setRadioList(prev=>prev.filter(x=>x.id!==t.id));
                }}>Delete</DeleteButton>
              </ListItem>
            ))}
          </Section>
        </SectionInner>
      )}

      {/* ── GAMING TAB ───────────────────────────────────────────────────── */}
      {activeTab==='gaming' && (
        <SectionInner>
          <Section>
            <SectionHeader>{editingCard?'Edit Card':'Add Gaming Card'}</SectionHeader>
            <Form onSubmit={async(e)=>{
              e.preventDefault(); if(loading) return;
              setGamingFormError('');
              if(gamingCard.listing_type===LISTING_TYPES.EBAY){
                const url=gamingCard.external_link.trim();
                let validUrl=false;
                if(/^https?:\/\//i.test(url)){ try{ new URL(url); validUrl=true; }catch{ validUrl=false; } }
                if(!validUrl){
                  setGamingFormError('Enter a valid eBay or store URL starting with http:// or https://');
                  return;
                }
              }
              setLoading(true);
              try{
                let image_url=editingCard?.image_url||null;
                let image_path=editingCard?.image_path||null;
                if(gamingCard.image){
                  const ext=gamingCard.image.name.split('.').pop();
                  const path='cards/'+Date.now()+'.'+ext;
                  const{error:upErr}=await supabase.storage.from('gaming-images').upload(path,gamingCard.image,{upsert:false});
                  if(upErr) throw upErr;
                  const{data:urlData}=supabase.storage.from('gaming-images').getPublicUrl(path);
                  image_url=urlData.publicUrl; image_path=path;
                  if(editingCard?.image_path) await supabase.storage.from('gaming-images').remove([editingCard.image_path]);
                }
                const payload={title:gamingCard.title.trim(),description:gamingCard.description.trim(),
                  listing_type:gamingCard.listing_type,
                  external_link:gamingCard.listing_type===LISTING_TYPES.EBAY?gamingCard.external_link.trim():null,
                  sort_order:parseInt(gamingCard.sort_order)||0,
                  image_url,image_path};
                let dbErr;
                if(editingCard){({error:dbErr}=await supabase.from('gaming_cards').update(payload).eq('id',editingCard.id));}
                else{({error:dbErr}=await supabase.from('gaming_cards').insert(payload));}
                if(dbErr) throw dbErr;
                alert(editingCard?'Card updated!':'Card added!');
                setGamingCard(EMPTY_GAMING_CARD);
                setEditingCard(null);
                const{data}=await supabase.from('gaming_cards').select('*').order('sort_order',{ascending:true});
                setGamingList(data||[]);
              }catch(err){alert('Save failed: '+err.message);}
              finally{setLoading(false);}
            }}>
              <InputGroup><Label>Title *</Label>
                <Input type="text" placeholder="e.g. Xbox One S Battlefield Edition" value={gamingCard.title}
                  onChange={e=>setGamingCard({...gamingCard,title:e.target.value})} required/>
              </InputGroup>
              <InputGroup><Label>Description</Label>
                <Textarea placeholder="Product details, price, specs..." value={gamingCard.description}
                  onChange={e=>setGamingCard({...gamingCard,description:e.target.value})}/>
              </InputGroup>
              <InputGroup>
                <Label>Listing Type *</Label>
                <RadioRow>
                  <RadioOption>
                    <input type="radio" name="listing_type" value={LISTING_TYPES.EBAY}
                      checked={gamingCard.listing_type===LISTING_TYPES.EBAY}
                      onChange={()=>{setGamingFormError('');setGamingCard({...gamingCard,listing_type:LISTING_TYPES.EBAY});}}/>
                    eBay / store listing
                  </RadioOption>
                  <RadioOption>
                    <input type="radio" name="listing_type" value={LISTING_TYPES.LOCAL}
                      checked={gamingCard.listing_type===LISTING_TYPES.LOCAL}
                      onChange={()=>{setGamingFormError('');setGamingCard({...gamingCard,listing_type:LISTING_TYPES.LOCAL,external_link:''});}}/>
                    Local only (contact by email)
                  </RadioOption>
                  <RadioOption>
                    <input type="radio" name="listing_type" value={LISTING_TYPES.SHOWCASE}
                      checked={gamingCard.listing_type===LISTING_TYPES.SHOWCASE}
                      onChange={()=>{setGamingFormError('');setGamingCard({...gamingCard,listing_type:LISTING_TYPES.SHOWCASE,external_link:''});}}/>
                    Showcase (not for sale)
                  </RadioOption>
                </RadioRow>
                {gamingCard.listing_type===LISTING_TYPES.LOCAL &&
                  <FieldHint>Buyers will be directed to email {CONTACT_EMAIL}.</FieldHint>}
              </InputGroup>
              {gamingCard.listing_type===LISTING_TYPES.EBAY && (
                <InputGroup><Label>eBay or store URL *</Label>
                  <Input type="text" placeholder="https://ebay.com/itm/..." value={gamingCard.external_link}
                    onChange={e=>setGamingCard({...gamingCard,external_link:e.target.value})} required/>
                </InputGroup>
              )}
              {gamingFormError && <FormError>{gamingFormError}</FormError>}
              <InputGroup><Label>Sort Order (lower = first)</Label>
                <Input type="number" value={gamingCard.sort_order}
                  onChange={e=>setGamingCard({...gamingCard,sort_order:e.target.value})}/>
              </InputGroup>
              <InputGroup><Label>Card Image (JPG / PNG)</Label>
                <Input type="file" accept="image/*"
                  onChange={e=>setGamingCard({...gamingCard,image:e.target.files?.[0]||null})}/>
              </InputGroup>
              <div style={{display:'flex',gap:'12px'}}>
                <Button type="submit" disabled={loading}>{loading?'Saving...':editingCard?'Update Card':'Add Card'}</Button>
                {editingCard&&<ArchiveButton type="button" onClick={()=>{setEditingCard(null);setGamingCard(EMPTY_GAMING_CARD);setGamingFormError('');}}>Cancel</ArchiveButton>}
              </div>
            </Form>
          </Section>

          <Section>
            <SectionHeader>Gaming Cards ({gamingList.length})</SectionHeader>
            {gamingList.length===0&&<p style={{color:'#718096',textAlign:'center',padding:'20px 0'}}>No cards yet.</p>}
            {gamingList.map(card=>(
              <ListItem key={card.id}>
                <div style={{display:'flex',alignItems:'center',gap:'14px',flex:1,minWidth:0}}>
                  {card.image_url&&<img src={card.image_url} alt={card.title}
                    style={{width:52,height:52,objectFit:'cover',borderRadius:8,flexShrink:0}}/>}
                  <div style={{minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                      <ItemTitle>{card.title}</ItemTitle>
                      <TypeBadge>{LISTING_TYPE_LABELS[card.listing_type]||card.listing_type}</TypeBadge>
                    </div>
                    {card.external_link&&<p style={{color:'#718096',fontSize:'0.78rem',margin:'3px 0 0',
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{card.external_link}</p>}
                  </div>
                </div>
                <ActionButtons>
                  <Button onClick={()=>{setEditingCard(card);setGamingCard(cardFromRow(card));setGamingFormError('');
                    window.scrollTo({top:0,behavior:'smooth'});}}>Edit</Button>
                  <DeleteButton onClick={async()=>{
                    if(!window.confirm('Delete "'+card.title+'"?')) return;
                    if(card.image_path) await supabase.storage.from('gaming-images').remove([card.image_path]);
                    await supabase.from('gaming_cards').delete().eq('id',card.id);
                    setGamingList(prev=>prev.filter(c=>c.id!==card.id));
                  }}>Delete</DeleteButton>
                </ActionButtons>
              </ListItem>
            ))}
          </Section>
        </SectionInner>
      )}

      {/* ── RESEARCH TAB ─────────────────────────────────────────────────── */}
      {activeTab==='research' && (
        <SectionInner>
          <Section>
            <SectionHeader>{RESEARCH_CATEGORY.title}</SectionHeader>
            <p style={{color:'#a0aec0',lineHeight:1.7,marginBottom:'8px'}}>
              {RESEARCH_CATEGORY.tagline}
            </p>
            <p style={{color:'#718096',fontSize:'0.85rem',lineHeight:1.6,margin:0}}>
              Internal only — research is not published to the public site and has no public route.
            </p>
          </Section>

          <Section>
            <SectionHeader>Research Library</SectionHeader>
            {RESEARCH_ARTICLES.length === 0 ? (
              <p style={{color:'#718096',textAlign:'center',marginTop:'20px'}}>
                No research pieces yet.
              </p>
            ) : (
              RESEARCH_ARTICLES.map((piece) => {
                const isOpen = openResearchSlug === piece.slug;
                return (
                  <div key={piece.slug} style={{marginBottom:'16px'}}>
                    <ListItem style={{marginBottom:0}}>
                      <div style={{flex:1,minWidth:0}}>
                        <ItemTitle>{piece.title}</ItemTitle>
                        <p style={{color:'#718096',fontSize:'0.8rem',margin:'4px 0 0'}}>
                          {formatResearchDate(piece.publishedAt)} · {piece.readingTime} · {piece.tags.join(', ')}
                        </p>
                      </div>
                      <ActionButtons>
                        <Button onClick={()=>setOpenResearchSlug(isOpen ? null : piece.slug)}>
                          {isOpen ? 'Close' : 'Read'}
                        </Button>
                      </ActionButtons>
                    </ListItem>
                    {isOpen && (
                      <div style={{background:'rgba(26,32,44,0.6)',border:'1px solid rgba(74,85,104,0.35)',
                        borderTop:'none',borderRadius:'0 0 12px 12px',padding:'28px'}}>
                        <ResearchArticle article={piece} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </Section>
        </SectionInner>
      )}

      </TabContent>
    </DashboardContainer>
  );
};

export default AdminDashboard;